/**
 * Ficha de um lançamento (conta a pagar / a receber).
 *
 * A lista mostra sete colunas; o resto do que foi digitado — cotação usada,
 * de qual caixa saiu, anexos, observações, datas de baixa — não aparecia em
 * lugar nenhum. Aqui é onde se vê tudo e se dá baixa sem caçar o ícone certo.
 */
import React from 'react';
import {
  X, CheckCircle, Clock, AlertTriangle, Trash2, FileDown,
  Paperclip, Handshake, Undo2,
} from 'lucide-react';
import { Anexo, fmt, fmtData, linkAnexo } from './tipos';

export interface LancamentoDetalhe {
  _id: string;
  tipo: 'pagar' | 'receber';
  descricao: string;
  valor: number;
  vencimento: string;
  categoria: string;
  fornecedor?: string;
  cliente?: string;
  status: string;
  observacoes?: string;
  criadoPor?: string;
  createdAt?: string;
  moeda?: string;
  cotacao?: number;
  valorBase?: number;
  fonteId?: string;
  fonteNome?: string;
  reembolsavel?: boolean;
  reembolsado?: boolean;
  dataPagamento?: string;
  dataRecebimento?: string;
  patrocinioId?: string;
  anexos?: Anexo[];
}

const CORES_STATUS: Record<string, string> = {
  pago: 'bg-green-100 text-green-700',
  recebido: 'bg-green-100 text-green-700',
  pendente: 'bg-yellow-100 text-yellow-700',
  vencido: 'bg-red-100 text-red-700',
};

const IconeStatus: React.FC<{ status: string }> = ({ status }) => {
  if (status === 'pago' || status === 'recebido') return <CheckCircle className="w-4 h-4" />;
  if (status === 'vencido') return <AlertTriangle className="w-4 h-4" />;
  return <Clock className="w-4 h-4" />;
};

/** Rótulo + valor, alinhados; some quando não há o que mostrar. */
const Campo: React.FC<{ rotulo: string; children?: React.ReactNode; sempre?: boolean }> =
  ({ rotulo, children, sempre }) => {
    const vazio = children === undefined || children === null || children === '' || children === '-';
    if (vazio && !sempre) return null;
    return (
      <div>
        <p className="text-[11px] text-gray-400 uppercase tracking-wide">{rotulo}</p>
        <p className="text-sm text-gray-900 mt-0.5">{vazio ? '—' : children}</p>
      </div>
    );
  };

export const DetalheLancamento: React.FC<{
  conta: LancamentoDetalhe;
  moedaBase: string;
  userEmail: string;
  onFechar: () => void;
  onBaixar: () => void;
  onExcluir: () => void;
  onEmitirCobranca?: () => void;
}> = ({ conta, moedaBase, userEmail, onFechar, onBaixar, onExcluir, onEmitirCobranca }) => {
  const moeda = conta.moeda || moedaBase;
  const outraMoeda = moeda !== moedaBase;
  const liquidado = conta.status === 'pago' || conta.status === 'recebido';
  const ehPagar = conta.tipo === 'pagar';
  const dataBaixa = ehPagar ? conta.dataPagamento : conta.dataRecebimento;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onFechar}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={e => e.stopPropagation()}>

        {/* Cabeçalho */}
        <div className={`px-6 py-5 rounded-t-2xl text-white bg-gradient-to-r ${ehPagar ? 'from-red-600 to-rose-700' : 'from-green-600 to-emerald-700'}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-medium opacity-80 uppercase tracking-wide">
                {ehPagar ? 'Conta a pagar' : 'Conta a receber'}
              </p>
              <h3 className="text-xl font-bold mt-0.5 break-words">{conta.descricao || 'Sem descrição'}</h3>
              <p className="text-2xl font-bold mt-2">{fmt(conta.valor, moeda)}</p>
              {outraMoeda && (
                <p className="text-sm opacity-80">
                  {fmt(conta.valorBase ?? conta.valor, moedaBase)} na cotação do lançamento
                  {conta.cotacao ? ` (1 ${moeda} = ${conta.cotacao} ${moedaBase})` : ''}
                </p>
              )}
            </div>
            <button onClick={onFechar} className="p-1.5 hover:bg-white/20 rounded-lg flex-shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Situação */}
          <div className="flex flex-wrap items-center gap-2">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${CORES_STATUS[conta.status] || 'bg-gray-100 text-gray-700'}`}>
              <IconeStatus status={conta.status} /> {conta.status}
            </span>
            {conta.reembolsavel && !conta.reembolsado && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-full px-2.5 py-1">
                <Undo2 className="w-3 h-3" /> a devolver
              </span>
            )}
            {!!conta.patrocinioId && (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-1">
                <Handshake className="w-3 h-3" /> patrocínio
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Campo rotulo="Vencimento" sempre>{fmtData(conta.vencimento)}</Campo>
            <Campo rotulo={ehPagar ? 'Pago em' : 'Recebido em'}>
              {dataBaixa ? fmtData(dataBaixa) : undefined}
            </Campo>
            <Campo rotulo="Categoria">{conta.categoria}</Campo>
            <Campo rotulo={ehPagar ? 'Fornecedor' : 'Cliente'}>
              {ehPagar ? conta.fornecedor : conta.cliente}
            </Campo>
            <Campo rotulo={ehPagar ? 'Saiu de' : 'Entrou em'}>{conta.fonteNome}</Campo>
            <Campo rotulo="Moeda" sempre>
              {moeda}{outraMoeda && conta.cotacao ? ` · cotação ${conta.cotacao}` : ''}
            </Campo>
            <Campo rotulo="Lançado por">{conta.criadoPor}</Campo>
            <Campo rotulo="Lançado em">
              {conta.createdAt ? fmtData(conta.createdAt.slice(0, 10)) : undefined}
            </Campo>
          </div>

          {!!conta.observacoes && (
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-1">Observações</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-xl p-3 border border-gray-100">
                {conta.observacoes}
              </p>
            </div>
          )}

          {!!conta.anexos?.length && (
            <div>
              <p className="text-[11px] text-gray-400 uppercase tracking-wide mb-2">
                Anexos ({conta.anexos.length})
              </p>
              <div className="space-y-1.5">
                {conta.anexos.map((a, i) => (
                  <a key={i} href={linkAnexo(a.url, userEmail)} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg px-2 py-1.5">
                    <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{a.nome}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Ações */}
        <div className="flex flex-wrap items-center justify-between gap-2 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
          <button onClick={onExcluir}
            className="px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-xl flex items-center gap-1.5">
            <Trash2 className="w-4 h-4" /> Excluir
          </button>
          <div className="flex flex-wrap items-center gap-2">
            {!ehPagar && onEmitirCobranca && (
              <button onClick={onEmitirCobranca}
                className="px-3 py-2 text-sm font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-xl flex items-center gap-1.5">
                <FileDown className="w-4 h-4" /> Emitir cobrança
              </button>
            )}
            {!liquidado && (
              <button onClick={onBaixar}
                className={`px-4 py-2 text-sm font-semibold text-white rounded-xl flex items-center gap-1.5 bg-gradient-to-r ${ehPagar ? 'from-red-600 to-rose-700' : 'from-green-600 to-emerald-700'}`}>
                <CheckCircle className="w-4 h-4" />
                {ehPagar ? 'Marcar como pago' : 'Marcar como recebido'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalheLancamento;
